import React, { createContext, useContext, useEffect, useState } from "react";
import {
    AUTH_REGION,
    AUTH_USER_POOL_SECRET,
    AUTH_USER_POOL_WEB_CLIENT_ID,
} from '../utils/config/configs';
import { Auth } from "aws-amplify";
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from "react-i18next";
// import logger from "../server/server";
import { AwsConfigAuth } from '../utils/config/auth';
import AuthService from '../utils/config/AuthService';
import openNotificationWithIcon from "../utils/hooks/openNotificationWithIcon";

// Configuração do Amplify
Auth.configure(AwsConfigAuth);

// Criação do Contexto
const AuthContext = createContext(null);

const authService = new AuthService(
    AUTH_USER_POOL_WEB_CLIENT_ID,
    AUTH_USER_POOL_SECRET,
    AUTH_REGION,
);

// Hook personalizado
export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuthContext deve ser usado dentro de um AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const { t } = useTranslation();
    const [user, setUser] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [userSignIn, setUserSignIn] = useState(null);
    const [isLogging, setIsLogging] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
    const [sendedEmailToRecoverPassword, setSendedEmailToRecoverPassword] = useState(false);
    const [smsMfa, setSmsMfa] = useState(false);
    const [token, setToken] = useState(null);

    const [zones, setZones] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [tags, setTags] = useState([]);

    useEffect(() => {
        // Aqui você pode definir a lógica para pegar o usuário se ele já estiver autenticado
        const loadUserFromLocalStorage = () => {
            const token = localStorage.getItem('unified_videowall_cascais_token');
            if (token) {
                // Recupera outros dados do localStorage
                const storedUser = localStorage.getItem('unified_videowall_cascais_user');
                const storedTenant = localStorage.getItem('unified_videowall_cascais_tenant');
                
                if (storedUser && storedUser !== 'undefined') {
                    setUser(JSON.parse(storedUser));
                }
                
                if (storedTenant && storedTenant !== 'undefined') {
                    setTenant(JSON.parse(storedTenant)?.[0]);
                }
            }
        };
        loadUserFromLocalStorage();
    }, []);

    // Função para buscar o usuário autenticado
    const fetchUser = async (user) => {
        try {
            let userDetails;
            if (user) {
                userDetails = user;
            } else {
                userDetails = await authService.getUser(localStorage.getItem('unified_videowall_cascais_token'));
            }

            // localStorage.setItem('user', JSON.stringify(userDetails));

            // Set tags
            // const tags = extractAttributeValue(userDetails, 'custom:tags');
            const tags = userDetails?.['custom:tags'];
            localStorage.setItem('unified_videowall_cascais_tags', tags);
            if (tags !== undefined) {
                const localStorageTagsParse = JSON.parse(tags);
                setTags(localStorageTagsParse);
            };

            // Set zones
            const zones = userDetails?.['custom:zones'];
            // const zones = extractAttributeValue(userDetails, 'custom:zones');
            localStorage.setItem('unified_videowall_cascais_zones', zones);
            if (zones !== undefined) {
                const localStorageZonesParse = JSON.parse(zones);
                setZones(localStorageZonesParse);
            };

            // Set permissions
            const permissions = userDetails?.['custom:permissions'];
            // const permissions = extractAttributeValue(userDetails, 'custom:permissions');
            localStorage.setItem('unified_videowall_cascais_permissions', permissions);
            if (permissions !== undefined) {
                const localStoragePermissionsParse = JSON.parse(permissions);
                setPermissions(localStoragePermissionsParse);
            };

            // Set tenant
            const tenant = userDetails?.['custom:tenant'];
            // const tenant = extractAttributeValue(userDetails, 'custom:tenant');
            localStorage.setItem('unified_videowall_cascais_tenant', tenant);

            setUser(userDetails);
            setTenant(tenant ? JSON.parse(tenant)?.[0] : null);
            setIsAuthenticating(false);
            setIsError(false);
            setSmsMfa(false);

            // logger.info('User ' + userSignIn?.['cognito:username'] + ' logged in successfully! Tenant: ' + tenant);
        } catch (error) {
            console.log("error:   ", error)
            if (error === 'The user is not authenticated') {
                setIsAuthenticating(true);
                openNotificationWithIcon('warning', t('warning'), t('firstLogin'));
            } else if (error === 'The user is not authenticated') {
                setSmsMfa(true);
                openNotificationWithIcon('success', t('success'), t('codeSentByMessage'));
            }
            setIsError(true);
        }
    };

    // Funções de login e logout
    const login = async (user) => {
        try {
            setIsLogging(true);

            const response = await authService.authenticateUser(user.username, user.password);

            if (response?.ChallengeName === "NEW_PASSWORD_REQUIRED") {
                setUserSignIn(response); // Store user sign-in info
                setIsAuthenticating(true);
                openNotificationWithIcon('warning', t('warning'), t('firstLogin'));
            } else if (response?.ChallengeName === "SMS_MFA" || response?.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
                setSmsMfa(true);
                openNotificationWithIcon('success', t('success'), t('codeSentByMessage'));
            } else {
                // If authentication is successful, continue processing
                const login_response_tokens = response.AuthenticationResult;
                setToken(login_response_tokens);
                localStorage.setItem('unified_videowall_cascais_token', login_response_tokens?.AccessToken);
                localStorage.setItem('unified_videowall_cascais_refreshToken', login_response_tokens?.RefreshToken);

                // Decodes the IdToken for the login response. It contains the user info
                const decoded_id_token = jwtDecode(login_response_tokens.IdToken);
                fetchUser(decoded_id_token);
            }
        } catch (error) {
            setIsError(true);
            openNotificationWithIcon('error', t('error'), t('incorrectFields'));
            // logger.error('User ' + user?.Username + ' tried to log in but fields were incorrect! Tenant: ' + tenant)
        } finally {
            setIsLogging(false);
        }
    };


    const logout = async () => {
        try {
            console.log("logout")
            await clearLocalStorage();
            setUser(null);
            await Auth.signOut();
            // logger.info('User ' + user?.Username + ' logged out successfully! Tenant: ' + tenant);
        } catch (error) {
            console.error('Erro no logout', error);
            // logger.error('User: ' + user?.Username + ' Tenant: ' + tenant + ', Error on log out');
        }
    };

    const authenticateUser = async (user) => {
        try {
            if (user?.newPassword === user?.repeatNewPassword) {
                const session = userSignIn.Session;  // A sessão é retornada no desafio
                await authService.completeNewPassword(user.username, user.newPassword, session);
                openNotificationWithIcon('success', t('success'), t('User Authenticated'));
                setIsAuthenticating(false);
            } else {
                openNotificationWithIcon('error', t('error'), t('passwordNoMatch'));
            }
        } catch (error) {
            console.error('Error changing password', error);
            openNotificationWithIcon('error', t('error'), t('passwordNotValid'));
            // logger.error('User: ' + user?.Username + ' Tenant: ' + tenant + ', Error changing password');
        }
    }

    const sendEmailToRecoverPassword = async (user) => {
        await authService.forgotPassword(user.username);
        openNotificationWithIcon('success', t('success'), t('emailSentCode'));
        setSendedEmailToRecoverPassword(true);//A função retorna sucesso se o e-mail for enviado
    }

    const recoverPassword = async (user) => {
        try {
            if (user?.newPassword === user?.repeatNewPassword) {
                await authService.confirmForgotPassword(user.username, user.passwordCode, user.newPassword);
                // Redirecionar o usuário para a tela de login ou fazer o login automaticamente
                setIsRecoveringPassword(false);
                setSendedEmailToRecoverPassword(false);
                openNotificationWithIcon('success', t('success'), t('Passord Changed'));
            } else {
                openNotificationWithIcon('error', t('error'), t('passwordNoMatch'));
            }
        } catch (error) {
            // openNotificationWithIcon('error', t('error'), t('errorResetPassword'));
        }
    }

    const loginWithMfa = async (user) => {
        try {
            await Auth.confirmSignIn(
                userSignIn,   // O objeto 'user' obtido do Auth.signIn
                user.mfaCode,   // Código MFA fornecido pelo usuário
                "SMS_MFA" // Desafio MFA (SMS_MFA ou SOFTWARE_TOKEN_MFA)
            );
            fetchUser();
            // O usuário está autenticado
        } catch (err) {
            console.log(err);
        }
    }

    const checkToken = async () => {
        const token = localStorage.getItem('unified_videowall_cascais_token');
        if (!token) {
            console.warn("Token not found in localStorage");
            return false;
        }

        try{
            const decodedToken = jwtDecode(token); // Decodifica o token
            const actualDate = Math.floor(Date.now() / 1000);
            if (decodedToken.exp < actualDate) {
                try {
                    console.log("Token expired, refreshing...");
                    const refreshToken = localStorage.getItem('unified_videowall_cascais_refreshToken');
                    const newTokens = await authService.renewToken(refreshToken); // Renova o token
                    localStorage.setItem('unified_videowall_cascais_token', newTokens.AccessToken);
                    localStorage.setItem('unified_videowall_cascais_refreshToken', newTokens.RefreshToken);
                    return true;
                } catch (error) {
                    console.error("Error refreshing token:", error);
                    return false;
                }
            }
        } catch (error) {
            // Tratamento do erro de refresh token expirado ou inválido
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                if (errorData.error === "invalid_grant") {
                    // Refresh token expirado ou inválido
                    logout();
                }
            }
        }
        return true; // O token ainda é válido
    };

    const clearLocalStorage = () => {
        return new Promise((resolve, reject) => {
            try {
                localStorage.removeItem('unified_videowall_cascais_tenant');
                localStorage.removeItem('unified_videowall_cascais_token');
                localStorage.removeItem('unified_videowall_cascais_tags');
                localStorage.removeItem('unified_videowall_cascais_zones');
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    };

    const value = {
        user,
        zones,
        permissions,
        tags,
        tenant,
        login,
        logout,
        isLogging,
        isError,
        isAuthenticating,//Authenticate user
        authenticateUser,
        userSignIn,
        smsMfa,//Login with MFA
        loginWithMfa,
        isRecoveringPassword,//Recover password
        sendedEmailToRecoverPassword,
        sendEmailToRecoverPassword,
        setIsRecoveringPassword,
        recoverPassword,
        checkToken,//Validate token
        token
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};